import { UserIcon } from "lucide-react";
import * as React from "react";

import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage,
  AvatarGroup,
  AvatarGroupCount,
} from "@/shared/components/primitives/avatar";
import { cn } from "@/shared/lib/utils";

type AvatarSize = "xs" | "sm" | "default" | "lg";
type AvatarShape = "circle" | "square";

/**
 * Deterministic hash → two HSL hues separated by 60°, producing a warm gradient pair that is visually
 * distinct per name without requiring any external service.
 */
function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (str.charCodeAt(i) + ((hash << 5) - hash)) | 0;
  }
  return hash;
}

function getGradientStyle(name: string): React.CSSProperties {
  const hash = Math.abs(hashCode(name));
  const h1 = hash % 360;
  const h2 = (h1 + 40) % 360;
  return {
    background: `linear-gradient(135deg, hsl(${h1}, 55%, 82%), hsl(${h2}, 60%, 70%))`,
    color: `hsl(${h1}, 45%, 22%)`,
  };
}

function getInitials(name: string, size: AvatarSize): string {
  const maxChars = size === "xs" ? 1 : 2;
  return name
    .trim()
    .split(/\s+/)
    .slice(0, maxChars)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

export interface ColocaAvatarProps {
  /**
   * URL of the image to display. When absent or on load error the component falls back to initials derived
   * from `name`.
   */
  src?: string;
  /**
   * Full name of the person or organization. Used to derive the initials shown in the fallback and to seed
   * the gradient color — the same name always produces the same color.
   */
  name?: string;
  /** Accessible label for the image. Defaults to `name`. */
  alt?: string;
  size?: AvatarSize;
  /**
   * Visual shape of the avatar container. - `"circle"` — fully rounded, for people and individual users. -
   * `"square"` — squircle (25% border-radius), for organizations and entities.
   */
  shape?: AvatarShape;
  /**
   * Status indicator rendered in the bottom-right corner. Pass a Lucide icon for an icon badge or leave empty
   * for a plain dot (e.g. online / offline indicator).
   */
  badge?: React.ReactNode;
  className?: string;
}

/**
 * User or organization avatar with automatic gradient fallback.
 *
 * When no `src` is provided — or when the image fails to load — the component derives initials from `name`
 * and renders them over a deterministic gradient background unique to that name. The same name always
 * produces the same color, so avatars stay consistent across the UI.
 *
 * Use `shape="circle"` for people and `shape="square"` for organizations. Use `badge` to overlay a status
 * indicator in the bottom-right corner.
 */
export function ColocaAvatar({
  src,
  name,
  alt,
  size = "default",
  shape = "circle",
  badge,
  className,
}: ColocaAvatarProps) {
  const initials = name ? getInitials(name, size) : undefined;
  const gradientStyle = name ? getGradientStyle(name) : undefined;

  return (
    <Avatar size={size} shape={shape} className={className}>
      {src && <AvatarImage src={src} alt={alt ?? name} />}
      <AvatarFallback className={cn(name && "font-medium")} style={gradientStyle}>
        {initials ?? <UserIcon className="size-[45%]" />}
      </AvatarFallback>
      {badge != null && <AvatarBadge>{badge}</AvatarBadge>}
    </Avatar>
  );
}

export { AvatarGroup as ColocaAvatarGroup, AvatarGroupCount as ColocaAvatarGroupCount };
